/**
 * Microcourse Studio — 独立 HTML 导出
 * 将 MicrocourseProject 生成为可独立运行的 reveal.js HTML 文件
 */

import type { MicrocourseProject, MicroSlide, MicroElement } from '@ai-tutor/shared'
import { normalizeQuizContent } from '@ai-tutor/shared'

// ─── Element → HTML ────────────────────────────────────────────────

function elementToHTML(el: MicroElement): string {
  switch (el.type) {
    case 'heading': {
      const tag = `h${el.level ?? 2}`
      return `<${tag}>${escapeHtml(el.content)}</${tag}>`
    }
    case 'paragraph':
      return `<p>${escapeHtml(el.content)}</p>`
    case 'quote':
      return `<blockquote>${escapeHtml(el.content)}${el.citation ? `<cite>— ${escapeHtml(el.citation)}</cite>` : ''}</blockquote>`
    case 'list':
      return el.content // already HTML
    case 'code':
      return `<pre><code>${escapeHtml(el.content)}</code></pre>`
    case 'math':
      return `<div class="math">${el.displayMode ? `$$${el.content}$$` : `\\(${el.content}\\)`}</div>`
    case 'image':
      return `<figure><img src="${escapeAttr(el.content)}" alt="${escapeAttr(el.alt ?? '')}" ${el.width ? `style="width:${el.width}"` : ''} />${el.caption ? `<figcaption>${escapeHtml(el.caption)}</figcaption>` : ''}</figure>`
    case 'video':
      return `<video src="${escapeAttr(el.content)}" controls ${el.poster ? `poster="${escapeAttr(el.poster)}"` : ''}></video>`
    case 'audio':
      return `<audio src="${escapeAttr(el.content)}" controls></audio>`
    case 'iframe':
      return `<iframe src="${escapeAttr(el.content)}" style="width:100%;height:${el.height ?? 400}px;border:none;" sandbox="allow-scripts allow-same-origin"></iframe>`
    case 'table':
      return el.content // already HTML table
    case 'divider':
      return '<hr />'
    case 'spacer':
      return `<div style="height:${el.height ?? 24}px"></div>`
    case 'geometry':
      return `<div class="geometry-placeholder">[几何图形: ${escapeHtml(el.title ?? '')}]</div>`
    case 'svgExplain':
      return `<div class="svg-placeholder">[SVG 讲解: ${escapeHtml(el.title ?? '')}]</div>`
    case 'quiz':
      return quizToHTML(el)
    case 'excalidraw':
      return excalidrawToHTML(el)
    default:
      return ''
  }
}

function excalidrawToHTML(el: MicroElement & { type: 'excalidraw' }): string {
  // 优先使用预渲染的静态 SVG，否则显示占位符
  if (el.renderedSvg) {
    return `<div class="excalidraw-embed" style="width:100%;max-height:${el.height ?? 420}px;overflow:hidden;">${el.renderedSvg}</div>`
  }
  return `<div class="excalidraw-placeholder" style="border:1px dashed #94a3b8;border-radius:8px;padding:40px;text-align:center;color:#64748b;">[手绘白板: ${escapeHtml(el.title ?? 'Excalidraw')}]</div>`
}

function quizToHTML(el: MicroElement & { type: 'quiz' }): string {
  const q = normalizeQuizContent(el.content)
  let html = `<div class="quiz-block"><strong>${escapeHtml(q.stem)}</strong>`

  if ((q.type === 'choice' || q.type === 'multi_choice') && q.options) {
    html += '<ul class="quiz-options">'
    for (const opt of q.options) {
      html += `<li>${escapeHtml(opt.label)}. ${escapeHtml(opt.content)}</li>`
    }
    html += '</ul>'
  } else if (q.type === 'true_false') {
    html += '<div class="quiz-tf"><span>✓ 正确</span> <span>✗ 错误</span></div>'
  } else if (q.type === 'fill_blank') {
    html += '<div class="quiz-fill">_______________</div>'
  }

  if (q.analysis) {
    html += `<p class="quiz-explanation"><em>解析：${escapeHtml(q.analysis)}</em></p>`
  }

  html += '</div>'
  return html
}

// ─── Slide → reveal.js section ─────────────────────────────────────

function slideToSection(slide: MicroSlide): string {
  const attrs: string[] = []
  if (slide.transition) attrs.push(`data-transition="${slide.transition}"`)
  if (slide.background?.value) {
    if (slide.background.type === 'color') {
      attrs.push(`data-background-color="${slide.background.value}"`)
    } else if (slide.background.type === 'image') {
      attrs.push(`data-background-image="${slide.background.value}"`)
    }
  }

  const content = slide.elements.map(elementToHTML).join('\n')
  const notes = slide.notes ? `<aside class="notes">${escapeHtml(slide.notes)}</aside>` : ''

  return `  <section ${attrs.join(' ')}>\n${content}\n${notes}  </section>`
}

// ─── Full HTML Document ────────────────────────────────────────────

export interface GenerateHTMLOptions {
  /** 是否包含 MathJax (KaTeX) 支持 */
  includeMath?: boolean
  /** 是否包含自动播放脚本 */
  includeAutoplay?: boolean
  /** 自定义 CSS */
  customCSS?: string
}

export function generateStandaloneHTML(
  project: MicrocourseProject,
  options: GenerateHTMLOptions = {}
): string {
  const { includeMath = true, includeAutoplay = true, customCSS = '' } = options

  const sections = project.slides
    .sort((a, b) => a.order - b.order)
    .map(slideToSection)
    .join('\n')

  const mathPlugin = includeMath
    ? `{ src: 'https://cdn.jsdelivr.net/npm/reveal.js@4/plugin/math/math.js', async: true },`
    : ''

  const mathConfig = includeMath
    ? `math: { katex: { version: 'latest' } },`
    : ''

  const autoplayScript = includeAutoplay
    ? `
    // Auto-play narration segments
    (function() {
      var deck = Reveal;
      var slides = ${JSON.stringify(project.slides.map(s => ({ id: s.id, segments: s.segments })))};
      var currentSlide = 0;
      var currentSeg = 0;
      var playing = false;
      var audio = new Audio();

      function playNext() {
        if (!playing) return;
        var slideData = slides[currentSlide];
        if (!slideData || currentSeg >= slideData.segments.length) {
          currentSlide++;
          currentSeg = 0;
          if (currentSlide >= slides.length) { playing = false; return; }
          deck.slide(currentSlide);
          setTimeout(playNext, 800);
          return;
        }
        var seg = slideData.segments[currentSeg];
        currentSeg++;
        if (seg.audioUrl) {
          audio.src = seg.audioUrl;
          audio.onended = playNext;
          audio.play().catch(function() { setTimeout(playNext, 1000); });
        } else {
          setTimeout(playNext, 2000);
        }
      }

      document.addEventListener('keydown', function(e) {
        if (e.key === 'p' && e.ctrlKey) {
          e.preventDefault();
          playing = !playing;
          if (playing) playNext();
          else audio.pause();
        }
      });
    })();`
    : ''

  const themeColors = project.customColors
    ? `:root { --mc-primary: ${project.customColors.primary}; --mc-accent: ${project.customColors.accent}; --mc-bg: ${project.customColors.bg}; }`
    : ''

  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(project.title)}</title>
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/reveal.js@4/dist/reveal.css" />
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/reveal.js@4/dist/theme/white.css" />
  ${includeMath ? '<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@latest/dist/katex.min.css" />' : ''}
  <style>
    ${themeColors}
    .reveal { font-family: ${project.fontFamily || "'Noto Sans SC', sans-serif"}; }
    .reveal .slides { text-align: left; }
    .reveal .slides section { padding: 20px 40px; }
    .quiz-block { background: #f0f7ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 16px; margin: 12px 0; }
    .quiz-options { list-style: upper-alpha; padding-left: 24px; }
    .quiz-options li { margin: 4px 0; }
    .quiz-explanation { color: #666; font-size: 0.85em; margin-top: 8px; }
    pre code { font-size: 0.8em; }
    blockquote { border-left: 4px solid #60a5fa; padding-left: 16px; font-style: italic; color: #555; }
    ${customCSS}
  </style>
</head>
<body>
  <div class="reveal">
    <div class="slides">
${sections}
    </div>
  </div>
  <script src="https://cdn.jsdelivr.net/npm/reveal.js@4/dist/reveal.js"></script>
  ${includeMath ? '<script src="https://cdn.jsdelivr.net/npm/reveal.js@4/plugin/math/math.js"></script>' : ''}
  <script>
    Reveal.initialize({
      hash: true,
      slideNumber: true,
      transition: '${project.transition || 'slide'}',
      ${mathConfig}
      ${includeMath ? 'plugins: [RevealMath.KaTeX],' : ''}
    });
    ${autoplayScript}
  </script>
</body>
</html>`
}

// ─── Download Helpers ──────────────────────────────────────────────

export function downloadHTML(project: MicrocourseProject): void {
  const html = generateStandaloneHTML(project)
  downloadFile(html, `${sanitizeFilename(project.title)}.html`, 'text/html')
}

export function downloadJSON(project: MicrocourseProject): void {
  const json = JSON.stringify(project, null, 2)
  downloadFile(json, `${sanitizeFilename(project.title)}.json`, 'application/json')
}

function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ─── Utils ─────────────────────────────────────────────────────────

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function escapeAttr(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function sanitizeFilename(name: string): string {
  return name.replace(/[<>:"/\\|?*]/g, '_').slice(0, 60) || 'microcourse'
}
